import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, ArrowBack } from '@material-ui/icons';
import { MCITestRecord } from '../../../constants/Records';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    actionCell: {
      width: 120,
    },
    emptyMessage: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
  }),
);

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: MCITestRecord[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (uniqueId: string) => void;
  onDelete: (uniqueId: string, timestamp: string) => void;
  onResetFlags: () => void;
}

const MCIRecordList: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  executing,
  loaded,
  deleted,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MCITestRecord | null>(null);

  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records`);
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/mci/create`);
  };

  const handleEdit = (record: MCITestRecord) => {
    const path = `/institutions/${institutionId}/users/${userId}/records/mci/${encodeURIComponent(record.timestamp)}/edit`;
    history.push(path);
  };

  const handleDeleteClick = (record: MCITestRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord && uniqueId) {
      onDelete(uniqueId, String(selectedRecord.timestamp));
    }
  };

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('ja-JP');
  };

  // 合計スコアを計算
  const calculateTotal = (answers?: number[]): number => {
    if (!answers || !Array.isArray(answers)) return 0;
    return answers.reduce((sum, a) => sum + (a || 0), 0);
  };

  // 回答を表示用にフォーマット
  const formatAnswers = (answers?: number[]): string => {
    if (!answers || !Array.isArray(answers)) return '-';
    return answers.join(', ');
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; MCIテスト
      </Typography>

      <div className={classes.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">MCIテスト一覧</Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </div>

      {executing && !loaded ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : records.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>MCIテスト記録はありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>テスト日時</TableCell>
                <TableCell>回答</TableCell>
                <TableCell>合計</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.timestamp}>
                  <TableCell>{formatTimestamp(record.timestamp)}</TableCell>
                  <TableCell>{formatAnswers(record.answers)}</TableCell>
                  <TableCell>{calculateTotal(record.answers)} 点</TableCell>
                  <TableCell className={classes.actionCell}>
                    <IconButton size="small" onClick={() => handleEdit(record)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(record)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="MCIテストの削除"
        content="このMCIテスト記録を削除しますか？"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default MCIRecordList;
